import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import '../../styles/SearchInput.css';

/**
 * Styled search input component matching CustomSelect styling
 * Supports collapsible mode for mobile where it shows as an icon and expands on click
 * 
 * @param {string} value - Current search value
 * @param {function} onChange - Called when value changes
 * @param {string} placeholder - Placeholder text
 * @param {boolean} showClear - Show clear button when value exists
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Whether the input is disabled
 * @param {boolean} collapsible - Whether search collapses to icon on mobile (default: true)
 */
function SearchInput({ 
  value = '', 
  onChange, 
  placeholder = 'Search...', 
  showClear = true,
  className = '',
  disabled = false,
  collapsible = true
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const handleClear = () => {
    if (onChange) {
      onChange('');
    }
    // Keep focus on input after clearing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleIconClick = () => {
    if (collapsible && !isExpanded) {
      setIsExpanded(true);
    }
  };

  const handleBlur = (e) => {
    // Only collapse if clicking outside the search container and there's no value
    if (collapsible && !value && containerRef.current && !containerRef.current.contains(e.relatedTarget)) {
      setIsExpanded(false);
    }
  };

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  // Keep expanded if there's a value
  useEffect(() => {
    if (value && collapsible) {
      setIsExpanded(true);
    }
  }, [value, collapsible]);

  const hasValue = value && value.length > 0;
  const showExpanded = !collapsible || isExpanded || hasValue;

  // Inline styles for consistent rendering
  const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '9px 12px',
    background: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--border-radius)',
    height: '42px',
    gap: '8px',
    boxSizing: 'border-box',
    transition: 'all 0.2s',
    cursor: showExpanded ? 'text' : 'pointer'
  };

  const inputStyle = {
    flex: 1,
    minWidth: 0,
    border: 'none',
    background: 'transparent',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    outline: 'none',
    padding: 0
  };

  return (
    <div 
      ref={containerRef}
      className={`custom-search ${disabled ? 'disabled' : ''} ${hasValue ? 'has-value' : ''} ${collapsible ? 'collapsible' : ''} ${showExpanded ? 'expanded' : 'collapsed'} ${className}`}
      onBlur={handleBlur}
    >
      <div className="custom-search-container" onClick={handleIconClick} style={containerStyle}>
        <Search size={18} className="custom-search-icon" style={{ flexShrink: 0, color: 'var(--text-secondary)' }} />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          className="custom-search-input"
          disabled={disabled}
          style={inputStyle}
        />
        {showClear && hasValue && (
          <button 
            type="button" 
            className="custom-search-clear" 
            onClick={handleClear}
            title="Clear search"
            disabled={disabled}
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

export default SearchInput;
